import {
	plainTextForClipboard,
	rewriteMentionsForClipboard,
} from "./mentionClipboard";
import { isRichTableCell, type ParsedTableCell } from "./parseTableElement";

type TableCellValue = string | ParsedTableCell;

/** The cell as a bare `<th>`/`<td>`, its mentions in their clipboard form. */
function clipboardCell(tag: "th" | "td", value: TableCellValue) {
	const cell = document.createElement(tag);
	if (isRichTableCell(value)) {
		// Clones, since the nodes are the ones mounted in the table on screen.
		cell.append(...value.nodes.map((node) => node.cloneNode(true)));
		rewriteMentionsForClipboard(cell);
	} else {
		cell.textContent = typeof value === "string" ? value : value.value;
	}
	return cell;
}

/**
 * A tab or line break inside a cell would split it into extra columns or rows
 * when pasted, so it reads as a space instead.
 */
const tabSeparatedCell = (cell: HTMLElement) =>
	plainTextForClipboard(cell).replace(/\s*[\t\r\n]+\s*/g, " ");

/**
 * The table as an HTML `<table>`, which spreadsheets and documents paste as
 * cells, and as tab-separated lines, which is what spreadsheets themselves put
 * on the clipboard as plain text, so it still pastes into columns wherever the
 * HTML is ignored.
 */
export function tableClipboardContent(
	headers: TableCellValue[],
	rows: TableCellValue[][],
): { html: string; plainText: string } {
	const table = document.createElement("table");
	const lines: string[] = [];

	if (headers.length > 0) {
		const row = table.createTHead().insertRow();
		const cells = headers.map((value) => clipboardCell("th", value));
		row.append(...cells);
		lines.push(cells.map(tabSeparatedCell).join("\t"));
	}

	const body = table.createTBody();
	for (const values of rows) {
		const cells = values.map((value) => clipboardCell("td", value));
		body.insertRow().append(...cells);
		lines.push(cells.map(tabSeparatedCell).join("\t"));
	}

	return { html: table.outerHTML, plainText: lines.join("\n") };
}
